import React, { useEffect, useMemo, useRef, useLayoutEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar";
import useJsonLd from "../../seo/useJsonLd";
import { buildOrganizationSchema, buildWebsiteSchema } from "../../seo/schemaBuilders";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  FiUserPlus,
  FiEdit3,
  FiShare2,
  FiShield,
  FiClock,
  FiHeadphones,
} from "react-icons/fi";
import ProductStory from "./sections/ProductStory";
import ThemesShowcase from "./sections/ThemesShowcase";
import ServicesSection from "./sections/ServicesSection";
import CustomerSection from "./sections/CustomerSection";
import TestimonialsSection from "./sections/TestimonialsSection";
import Footer from "./sections/Footer";
import HomeSplash from "./components/HomeSplash";
import SectionHeading from "./components/SectionHeading";

gsap.registerPlugin(ScrollTrigger);

/**
 * NOTE ON NUMBERS BELOW:
 * The stats in <StatsBand /> ("۵۰۰+ رستوران" و غیره) placeholder هستن.
 * قبل از انتشار حتما با اعداد واقعی خودتون جایگزین کنید — نمایش آمار غیرواقعی
 * به کاربر می‌تونه اعتماد رو خدشه‌دار کنه.
 */

const HomePage = () => {
  const navigate = useNavigate();
  const organizationSchema = useMemo(() => buildOrganizationSchema(), []);
  const websiteSchema = useMemo(() => buildWebsiteSchema(), []);
  useJsonLd("organization", organizationSchema);
  useJsonLd("website", websiteSchema);
  const rootRef = useRef(null);
  const heroRef = useRef(null);
  const heroBgRef = useRef(null);
  const heroBadgeRef = useRef(null);
  const heroTitleRef = useRef(null);
  const heroTextRef = useRef(null);
  const heroActionsRef = useRef(null);
  const heroTrustRef = useRef(null);
  const sectionsRef = useRef([]);
  const marqueeRef = useRef(null);
  const marqueeTweenRef = useRef(null);

  const logos = [
    "/images/brands/akbar.png",
    "/images/brands/Ali-Ghapoo.png",
    "/images/brands/mamanjoon.png",
    "/images/brands/nayeb.png",
    "/images/brands/rouhi.png",
    "/images/brands/shandiz.png",
  ];

  const setRef = (el, index) => {
    if (el) sectionsRef.current[index] = el;
  };

  // Scroll-in animations — wrapped in gsap.context so triggers are
  // properly created/killed on mount & unmount (avoids duplicate
  // ScrollTriggers on fast refresh / re-renders).
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const ctx = gsap.context(() => {
      sectionsRef.current.forEach((el) => {
        if (!el) return;
        gsap.fromTo(
          el,
          { y: 60, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 1,
            ease: "power3.out",
            scrollTrigger: {
              trigger: el,
              start: "top 80%",
              toggleActions: "play none none none",
            },
          }
        );
      });
      // Hero -> product story continuity: as the visitor scrolls the hero
      // out of view, it scales/fades with scroll progress (not a one-shot
      // exit) so it reads as the opening beat of the pinned story that
      // follows, rather than an unrelated block ending abruptly.
      if (heroRef.current) {
        gsap.to(heroRef.current, {
          scale: 1.06,
          opacity: 0,
          ease: "none",
          scrollTrigger: {
            trigger: heroRef.current,
            start: "top top",
            end: "bottom top",
            scrub: true,
          },
        });
      }
    }, rootRef);

    return () => ctx.revert();
  }, []);

  // Hero entrance — plays once on mount (not scroll-gated, since the hero
  // is already in view on load). The photo itself stays still: no looping
  // zoom or other decorative motion.
  useLayoutEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const els = [
      heroBadgeRef.current,
      heroTitleRef.current,
      heroTextRef.current,
      heroActionsRef.current,
      heroTrustRef.current,
    ].filter(Boolean);

    if (prefersReducedMotion) {
      gsap.set(els, { opacity: 1, y: 0 });
      return undefined;
    }

    const ctx = gsap.context(() => {
      gsap.set(els, { opacity: 0, y: 28 });
      const tl = gsap.timeline({ delay: 0.15, defaults: { ease: "power3.out" } });
      tl.to(heroBadgeRef.current, { opacity: 1, y: 0, duration: 0.6 })
        .to(heroTitleRef.current, { opacity: 1, y: 0, duration: 0.8 }, "-=0.35")
        .to(heroTextRef.current, { opacity: 1, y: 0, duration: 0.7 }, "-=0.45")
        .to(heroActionsRef.current, { opacity: 1, y: 0, duration: 0.6 }, "-=0.4")
        .to(heroTrustRef.current, { opacity: 1, y: 0, duration: 0.6 }, "-=0.35");
    }, heroRef);

    return () => ctx.revert();
  }, []);

  // Infinite logo marquee — pausable on hover/focus for accessibility
  // and so users can actually read a logo they're interested in.
  useLayoutEffect(() => {
    const el = marqueeRef.current;
    if (!el) return;

    const totalWidth = el.scrollWidth / 2;
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion) return;

    const tween = gsap.to(el, {
      x: -totalWidth,
      duration: 45,
      ease: "none",
      repeat: -1,
      modifiers: {
        x: (x) => {
          const value = parseFloat(x);
          return (((value % totalWidth) + totalWidth) % totalWidth) + "px";
        },
      },
    });

    marqueeTweenRef.current = tween;
    return () => tween.kill();
  }, []);

  const pauseMarquee = () => marqueeTweenRef.current?.pause();
  const resumeMarquee = () => marqueeTweenRef.current?.play();

  const handleStart = () => navigate("/auth/restaurant");
  const scrollToProductStory = () =>
    document.getElementById("product-story")?.scrollIntoView({ behavior: "smooth" });

  const trustPoints = [
    { icon: <FiClock />, label: "راه‌اندازی در کمتر از ۵ دقیقه" },
    { icon: <FiShield />, label: "بدون نیاز به کارت بانکی" },
    { icon: <FiHeadphones />, label: "پشتیبانی در تمام مراحل" },
  ];

  const steps = [
    {
      icon: <FiUserPlus />,
      title: "ثبت‌نام رایگان",
      text: "اطلاعات ساده‌ی رستورانت رو وارد کن و در کمتر از یک دقیقه حساب کاربریت رو بساز.",
    },
    {
      icon: <FiEdit3 />,
      title: "طراحی منو",
      text: "دسته‌بندی‌ها، عکس‌ها و قیمت‌ها رو اضافه کن و رنگ و فونت رو مطابق برندت انتخاب کن.",
    },
    {
      icon: <FiShare2 />,
      title: "انتشار و اشتراک‌گذاری",
      text: "با یک کلیک منو رو منتشر کن و لینک یا QR کد رو روی میزها و شبکه‌های اجتماعی بذار.",
    },
  ];

  return (
    <div ref={rootRef} className="overflow-x-hidden">
      <HomeSplash />
      <Navbar fullWidth />

      {/* ---------- HERO ---------- */}
      {/* The hero fades out as it scrolls away; this white ground (the
          story section's color) is what shows through it, so the fade leads
          straight into the next section. */}
      <div className="bg-white">
      <section ref={heroRef} style={{ transformOrigin: "50% 0%" }}>
        <div className="relative flex min-h-[calc(100svh-4rem)] max-h-[980px] items-center overflow-hidden px-5 py-16 sm:px-10 lg:px-20">
          {/* Full-resolution photo of a luxury lounge and bar (picjumbo, free
              for commercial use, no attribution required). */}
          <img
            ref={heroBgRef}
            src="/images/hero/hero-lounge-2560.jpg"
            srcSet="/images/hero/hero-lounge-1280.jpg 1280w, /images/hero/hero-lounge-2560.jpg 2560w"
            sizes="100vw"
            alt=""
            fetchpriority="high"
            className="absolute inset-0 h-full w-full object-cover"
            style={{ objectPosition: "50% 50%" }}
          />
          {/* Neutral dark overlay on the reading side (right, RTL) only, so the
              text has contrast while the photo keeps its natural tones. */}
          <div className="absolute inset-0 bg-gradient-to-l from-black/75 via-black/35 to-transparent" />

          <div className="relative z-10 flex w-full max-w-2xl flex-col items-start space-y-6 text-right md:space-y-7">
            <p
              ref={heroBadgeRef}
              className="text-sm font-semibold text-white/75 sm:text-base"
            >
              پلتفرم مدیریت هوشمند رستوران
            </p>

            <h1
              ref={heroTitleRef}
              className="text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl md:text-6xl"
            >
              مدیریت رستوران،
              <br />
              <span className="text-primary">در لحظه!</span>
            </h1>
            <p
              ref={heroTextRef}
              className="max-w-lg text-lg font-medium leading-8 text-white/90 sm:text-xl md:leading-10"
            >
              بدون نیاز به برنامه‌نویسی، فقط با چند کلیک رستورانت رو آنلاین کن
            </p>

            <div
              ref={heroActionsRef}
              className="flex w-full flex-col items-start justify-start gap-3 sm:w-auto sm:flex-row"
            >
              <button
                onClick={handleStart}
                className="min-h-12 w-full rounded-xl bg-secondary px-8 py-3 text-base font-semibold text-white transition-colors hover:bg-[#195a4c] focus:outline-none focus:ring-4 focus:ring-white/30 sm:w-auto"
              >
                رایگان شروع کنید
              </button>
              <button
                onClick={scrollToProductStory}
                className="min-h-12 w-full rounded-xl border border-white/80 px-8 py-3 text-base font-semibold text-white transition-colors hover:bg-white hover:text-accent focus:outline-none focus:ring-4 focus:ring-white/30 sm:w-auto"
              >
                دیدن امکانات
              </button>
            </div>

            <ul
              ref={heroTrustRef}
              className="flex max-w-lg flex-wrap items-start justify-start gap-x-6 gap-y-3 pt-2 text-sm text-white/90 md:text-base"
            >
              {trustPoints.map((t, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="text-[#ffd2af]" aria-hidden="true">{t.icon}</span>
                  {t.label}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
      </div>

      {/* ---------- PRODUCT STORY (pinned, scroll-scrubbed) ---------- */}
      <ProductStory />

      {/* ---------- THEME CATALOGUE (pinned, horizontal film strip) ---------- */}
      <ThemesShowcase />

      <main className="bg-white">

        <ServicesSection setRef={setRef} />

        {/* ---------- HOW IT WORKS ---------- */}
        <section id="services" className="max-w-7xl mx-auto px-4 py-16" ref={(el) => setRef(el, 6)}>
          <SectionHeading
            eyebrow="شروع کار"
            title="راه‌اندازی در سه مرحله‌ی ساده"
            highlight="سه مرحله‌ی ساده"
            subtitle="بدون نیاز به طراح یا برنامه‌نویس، منوی رستوران خودتون رو همین امروز آنلاین کنید"
            className="mb-12"
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((s, i) => (
              <div key={i} className="relative h-full rounded-3xl border border-[#e3eeea] bg-white p-6 shadow-[0_10px_30px_rgba(16,43,35,.06)] transition-shadow hover:shadow-[0_16px_40px_rgba(16,43,35,.12)]">
                <span className="absolute -top-4 -right-4 flex h-9 w-9 items-center justify-center rounded-full bg-primary font-bold text-white ring-4 ring-white">
                  {(i + 1).toLocaleString("fa-IR")}
                </span>
                <div className="mb-4 grid h-12 w-12 place-items-center rounded-2xl bg-primary_1 text-2xl text-primary" aria-hidden="true">{s.icon}</div>
                <h3 className="mb-2 text-lg font-bold text-[#102b23]">{s.title}</h3>
                <p className="leading-relaxed text-[#4a5a54]">{s.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ---------- STATS BAND ---------- */}
        {/* اعداد زیر نمونه‌ان — قبل از انتشار با آمار واقعی جایگزین کنید */}
        <StatsBand setRef={setRef} />

        {/* ---------- LOGOS MARQUEE ---------- */}
        <section className="overflow-hidden border-y border-[#e3eeea] bg-[#f3f9f7] py-12">
          <p className="mb-8 text-center text-sm font-semibold text-[#1F6D5C]">
            رستوران‌هایی که با بامیزبان آنلاین شدند
          </p>
          <div
            className="relative"
            onMouseEnter={pauseMarquee}
            onMouseLeave={resumeMarquee}
          >
            <div
              ref={marqueeRef}
              className="flex gap-16 items-center"
              role="list"
              aria-label="برندهای مشتری بامیزبان"
            >
              {[...logos, ...logos, ...logos].map((src, i) => (
                <div
                  key={i}
                  role="listitem"
                  className="flex items-center justify-center min-w-[140px] opacity-70 hover:opacity-100 transition"
                >
                  <img
                    src={src}
                    alt="لوگوی برند مشتری"
                    className="h-14 object-contain grayscale hover:grayscale-0 transition duration-500"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* بقیه سکشن‌ها */}


        <CustomerSection setRef={setRef} />
        <TestimonialsSection setRef={setRef} />

        {/* ---------- FINAL CTA ---------- */}
        {/* Final CTA repeats the story's brand green panel and food emojis
            as a closing bookend. The button's dark green text on orange
            keeps a 5.9:1 contrast ratio. */}
        <section className="mx-auto max-w-5xl px-4 py-20">
          <div className="relative rounded-[2.5rem] bg-secondary px-6 py-12 text-center sm:px-8 sm:py-14">
            {CTA_EMOJIS.map(({ emoji, className }) => (
              <span
                key={emoji}
                className={`pointer-events-none absolute select-none leading-none ${className}`}
                aria-hidden="true"
              >
                {emoji}
              </span>
            ))}
            <p className="mb-4 flex items-center justify-center gap-3 text-sm font-bold text-[#ffdcc4]">
              <span className="h-px w-8 bg-[#ffdcc4]/60" aria-hidden="true" />
              قدم بعدی
              <span className="h-px w-8 bg-[#ffdcc4]/60" aria-hidden="true" />
            </p>
            <h2 className="mb-3 text-2xl font-black text-white md:text-3xl">
              همین حالا منوی رستوران خودتون رو آنلاین کنید
            </h2>
            <p className="mb-8 text-white/85">
              ثبت‌نام رایگان است و در کمتر از ۵ دقیقه آماده‌ی استفاده‌ست
            </p>
            <button
              onClick={handleStart}
              className="min-h-12 rounded-xl bg-primary px-8 py-3 text-base font-bold text-[#102b23] transition-colors hover:bg-[#ff9a4a] focus:outline-none focus:ring-4 focus:ring-white/40"
            >
              رایگان شروع کنید
            </button>
          </div>
        </section>
      </main>

      <Footer tone="brand" />
    </div>
  );
};

// Food emojis around the final CTA panel, matching the product story.
const CTA_EMOJIS = [
  { emoji: "🍊", className: "-top-5 right-10 text-4xl" },
  { emoji: "🌶️", className: "top-8 left-8 hidden text-2xl sm:block" },
  { emoji: "🍕", className: "-bottom-5 left-14 text-4xl" },
  { emoji: "🍹", className: "bottom-8 right-8 hidden text-3xl sm:block" },
];

// Small, self-contained stats band. Kept as its own component so the
// placeholder numbers are easy to find and swap for real ones.
const StatsBand = ({ setRef }) => {
  const stats = [
    { value: "۵۰۰+", label: "رستوران فعال" },
    { value: "۵ دقیقه", label: "میانگین زمان راه‌اندازی" },
    { value: "٪۹۸", label: "رضایت مشتریان" },
  ];

  return (
    <section className="mx-auto max-w-5xl px-4 py-12" ref={(el) => setRef(el, 20)}>
      <div className="grid grid-cols-1 gap-8 rounded-[2.5rem] bg-secondary px-6 py-10 text-center sm:grid-cols-3 sm:divide-x sm:divide-x-reverse sm:divide-white/15">
        {stats.map((s, i) => (
          <div key={i}>
            <p className="text-3xl font-black text-white md:text-4xl">{s.value}</p>
            <p className="mt-2 text-sm font-semibold text-[#ffdcc4] md:text-base">{s.label}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

export default HomePage;
